import PropTypes from "prop-types";
import React, { useEffect } from "react";
import {
  Button,
  ButtonToolbar,
  Form,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  FormLabel,
} from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import genericTranslations from "../../../../utils/genericTranslations.js";
import { selectMalfunctionsCategories } from "../../selectors.js";

const styles = {
  buttonContainer: {
    display: "flex",
    paddingBottom: "10px",
    flexDirection: "column",
    maxHeight: "440px",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
  },
  button: {
    paddingTop: "20px",
    marginTop: "14px",
    minWidth: "400px",
    height: "70px",
    borderRadius: "4px",
  },
  text: {
    fontSize: 18,
  },
};

const messages = {
  vehicle: {
    id: "modules.Malfunctions.components.MalfunctionsList.vehicle",
    defaultMessage: "Vehicle-number",
  },
  category: {
    id: "modules.Malfunctions.components.MalfunctionsForm.category",
    defaultMessage: "Select a category",
  },
  comments: {
    id: "modules.Malfunctions.components.MalfunctionsForm.comments",
    defaultMessage: "Enter a comment (optional)",
  },
};

const MalfunctionsForm = ({
  handleChange,
  handleSubmit,
  errors,
  isSubmitting,
  values,
  setFieldValue,
}) => {
  const { identifier } = useParams();

  const categories = useSelector(selectMalfunctionsCategories);

  useEffect(() => {
    if (values.category) {
      setFieldValue("vehicleNumber", identifier);
    }
    categories.forEach((cat) => {
      if (cat.value === values.category) {
        setFieldValue("vehicleType", cat.vehicleType);
      }
    });
  }, [values.category, identifier, setFieldValue, categories]);

  const handleChangeRadioButton = (value) => {
    setFieldValue("category", value);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group controlId="vehicleNumber" className="mb-3">
        <FormLabel>
          <FormattedMessage {...messages.vehicle} />
        </FormLabel>
        <FormControl
          type="text"
          autoComplete="off"
          value={identifier}
          disabled
          isInvalid={!identifier}
        />
      </Form.Group>

      <Form.Group controlId="category" className="mb-3">
        <FormLabel>
          <FormattedMessage {...messages.category} />
        </FormLabel>
        <ToggleButtonGroup
          type="radio"
          name="options"
          onChange={handleChangeRadioButton}
          style={styles.buttonContainer}
        >
          {categories?.map((cat) => (
            <ToggleButton
              key={cat.id}
              id={`radio-${cat.id}`}
              value={cat.value}
              variant={
                values.category === cat.value ? "primary" : "outline-secondary"
              }
              style={styles.button}
              checked={values.category === cat.value}
            >
              <span style={styles.text}>{cat.label}</span>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Form.Group>

      <Form.Group controlId="comment" className="mb-3">
        <FormLabel>
          <FormattedMessage {...messages.comments} />
        </FormLabel>
        <FormControl type="text" autoComplete="off" onChange={handleChange} />
      </Form.Group>

      <hr />

      <Form.Group controlId="formActions" className="mb-3">
        <ButtonToolbar>
          <Button
            type="submit"
            disabled={isSubmitting || Object.keys(errors).length > 0}
          >
            <FormattedMessage {...genericTranslations.save} />
          </Button>
        </ButtonToolbar>
      </Form.Group>
    </Form>
  );
};

MalfunctionsForm.propTypes = {
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  errors: PropTypes.object,
  values: PropTypes.object.isRequired,
};

export default MalfunctionsForm;
